import { useState, useEffect, useRef } from 'react';
import Navbar from '../components/Navbar';
import { Send, Loader2, Dumbbell, Apple, Heart, Brain, Trophy, TrendingUp, Calendar, Bookmark, Share2, History, Trash2 } from 'lucide-react';
import CoachIcon from '../components/icons/CoachIcon';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { aiAPI } from '../services/api';
import iCoachService from '../services/iCoachService';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const INITIAL_MESSAGE = {
  id: 1,
  type: 'ai',
  text: '👋 Hello! I\'m iCoach, your AI sports performance advisor.\n\n🎯 Choose a category below or ask me anything!',
  isComplete: true,
  imageUrl: null
};

const AskAI = () => {
  // User-scoped storage keys
  const userId = localStorage.getItem('userId') || 'anonymous';
  const msgKey = `icoach_messages_${userId}`;
  const sidKey = `icoach_sessionId_${userId}`;

  // Restore state from localStorage on mount
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(msgKey);
      if (!saved) return [INITIAL_MESSAGE];
      const parsed = JSON.parse(saved);
      return parsed.map(m => {
        if (m.isComplete) return m;
        if (m.text && m.text.length > 0) return { ...m, isComplete: true };
        return m;
      });
    } catch { return [INITIAL_MESSAGE]; }
  });
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(sidKey) || null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const chatContainerRef = useRef(null);
  const isUserNearBottom = useRef(true);
  const streamIntervalRef = useRef(null);
  const streamingMsgIdRef = useRef(null);
  const fullTextsRef = useRef({}); // messageId → full response text (survives streaming)

  const categories = [
    {
      id: 'training',
      name: 'Training Plans',
      icon: Dumbbell,
      color: 'hsl(var(--primary))',
      gradient: 'from-purple-500 to-purple-700',
      prompts: [
        'Create a 30-day training plan',
        'Warm-up routine for runners',
        'Strength training exercises',
        'HIIT workout suggestions'
      ]
    },
    {
      id: 'nutrition',
      name: 'Nutrition',
      icon: Apple,
      color: 'hsl(267, 55%, 45%)',
      gradient: 'from-purple-600 to-purple-800',
      prompts: [
        'Pre-game meal suggestions',
        'Protein intake for athletes',
        'Hydration tips',
        'Healthy snack ideas'
      ]
    },
    {
      id: 'recovery',
      name: 'Recovery',
      icon: Heart,
      color: 'hsl(267, 50%, 62%)',
      gradient: 'from-purple-400 to-purple-600',
      prompts: [
        'Post-workout recovery',
        'Stretching routine',
        'Sleep optimization',
        'Manage muscle soreness'
      ]
    },
    {
      id: 'mental',
      name: 'Mental Game',
      icon: Brain,
      color: 'hsl(267, 60%, 38%)',
      gradient: 'from-purple-700 to-purple-900',
      prompts: [
        'Pre-competition mindset',
        'Overcome performance anxiety',
        'Focus techniques',
        'Build confidence'
      ]
    },
    {
      id: 'performance',
      name: 'Performance',
      icon: TrendingUp,
      color: 'hsl(267, 51%, 56%)',
      gradient: 'from-purple-500 to-purple-700',
      prompts: [
        'Improve speed & agility',
        'Track my progress',
        'Analyze my stats',
        'Set realistic goals'
      ]
    },
    {
      id: 'technique',
      name: 'Technique',
      icon: Trophy,
      color: 'hsl(267, 55%, 75%)',
      gradient: 'from-purple-300 to-purple-500',
      prompts: [
        'Sport-specific drills',
        'Correct form tips',
        'Common mistakes',
        'Advanced techniques'
      ]
    }
  ];

  const scrollToBottom = () => {
    const container = chatContainerRef.current;
    if (!container || !isUserNearBottom.current) return;
    container.scrollTop = container.scrollHeight;
  };

  const handleScroll = () => {
    const container = chatContainerRef.current;
    if (!container) return;
    const threshold = 100;
    isUserNearBottom.current = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  };

  // Persist messages & sessionId to localStorage — always save full text
  useEffect(() => {
    const toSave = messages.map(msg => {
      const fullText = fullTextsRef.current[msg.id];
      if (fullText && !msg.isComplete) {
        return { ...msg, text: fullText, isComplete: true };
      }
      return msg;
    });
    localStorage.setItem(msgKey, JSON.stringify(toSave));
  }, [messages, msgKey]);

  useEffect(() => {
    if (sessionId) localStorage.setItem(sidKey, sessionId);
  }, [sessionId, sidKey]);

  // Scroll only when new messages are added (not during streaming)
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // On mount: check if a response completed while we were away, or poll if still loading
  useEffect(() => {
    const pending = iCoachService.consumePending();
    if (pending) {
      const { message, sessionId: newSessionId, imageUrl } = pending;
      if (newSessionId && !sessionId) setSessionId(newSessionId);
      try {
        const saved = JSON.parse(localStorage.getItem(msgKey) || '[]');
        if (saved.length > 0) {
          setMessages(saved);
          setLoading(false);
        }
      } catch {}
    } else if (iCoachService.isLoading()) {
      setLoading(true);
      const poll = setInterval(() => {
        const result = iCoachService.consumePending();
        if (result) {
          clearInterval(poll);
          const { message, sessionId: newSessionId, imageUrl } = result;
          if (newSessionId) setSessionId(newSessionId);
          try {
            const saved = JSON.parse(localStorage.getItem(msgKey) || '[]');
            if (saved.length > 0) {
              setMessages(saved);
            }
          } catch {}
          setLoading(false);
        }
      }, 500);
      return () => clearInterval(poll);
    } else {
      setMessages(prev => prev.filter(m => m.isComplete || (m.text && m.text.length > 0)));
      setLoading(false);
    }
  }, []);

  // Cleanup streaming interval on unmount
  useEffect(() => {
    return () => {
      if (streamIntervalRef.current) {
        clearInterval(streamIntervalRef.current);
      }
      // Save messages with full texts on unmount
      try {
        const saved = localStorage.getItem(msgKey);
        if (saved) {
          const msgs = JSON.parse(saved).map(msg => {
            const fullText = fullTextsRef.current[msg.id];
            if (fullText) return { ...msg, text: fullText, isComplete: true };
            if (!msg.isComplete && msg.type === 'ai' && (!msg.text || msg.text.length === 0)) {
              return msg;
            }
            return { ...msg, isComplete: true };
          });
          localStorage.setItem(msgKey, JSON.stringify(msgs));
        }
      } catch {}
    };
  }, []);

  // Streaming text effect — updates message text in chunks, not char-by-char
  const streamText = (fullText, messageId) => {
    if (streamIntervalRef.current) clearInterval(streamIntervalRef.current);
    streamingMsgIdRef.current = messageId;
    fullTextsRef.current[messageId] = fullText; // store full text for persistence
    let currentIndex = 0;
    const chunkSize = 8; // characters per tick
    const speed = 30; // ms per tick

    streamIntervalRef.current = setInterval(() => {
      if (currentIndex < fullText.length) {
        currentIndex = Math.min(currentIndex + chunkSize, fullText.length);
        const partial = fullText.substring(0, currentIndex);
        setMessages(prev => prev.map(msg =>
          msg.id === messageId ? { ...msg, text: partial, isComplete: false } : msg
        ));
      } else {
        clearInterval(streamIntervalRef.current);
        streamIntervalRef.current = null;
        streamingMsgIdRef.current = null;
        delete fullTextsRef.current[messageId];
        setMessages(prev => prev.map(msg =>
          msg.id === messageId ? { ...msg, text: fullText, isComplete: true } : msg
        ));
        setLoading(false);
      }
    }, speed);
  };

  const handleSend = async (messageText = null) => {
    const textToSend = messageText || inputText.trim();
    if (!textToSend || loading) return;

    setInputText('');
    setLoading(true);
    setSelectedCategory(null);
    isUserNearBottom.current = true;

    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: textToSend,
      isComplete: true
    };
    setMessages(prev => [...prev, userMessage]);

    // Add placeholder AI message
    const aiMessageId = Date.now() + 1;
    const aiMessagePlaceholder = {
      id: aiMessageId,
      type: 'ai',
      text: '',
      isComplete: false,
      imageUrl: null
    };
    setMessages(prev => [...prev, aiMessagePlaceholder]);

    try {
      // Call AI API via global service (survives navigation)
      const { message, sessionId: newSessionId, imageUrl } = await iCoachService.sendMessage(textToSend, sessionId);

      // Update session ID if new
      if (!sessionId) {
        setSessionId(newSessionId);
      }

      // Save full response immediately (so navigating away doesn't lose it)
      setMessages(prev => prev.map(msg =>
        msg.id === aiMessageId ? { ...msg, text: message, imageUrl: imageUrl || null, isComplete: false } : msg
      ));

      // Start streaming visual effect
      streamText(message, aiMessageId);
    } catch (error) {
      console.error('AI chat error:', error);
      toast.error('Failed to get AI response. Please try again.');
      
      // Update with error message
      setMessages(prev => prev.map(msg => 
        msg.id === aiMessageId 
          ? { ...msg, text: 'I apologize, but I\'m having trouble responding right now. Please try again in a moment.', isComplete: true } 
          : msg
      ));
      setLoading(false);
    }
  };

  const handleSaveResponse = (message) => {
    toast.success('Response saved to your library!');
    // TODO: Implement save to user's saved responses
  };

  const handleShareResponse = async (message) => {
    const shareText = message.text;
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'iCoach AI Response - SportiZn',
          text: shareText
        });
        return;
      }
      await navigator.clipboard.writeText(shareText);
      toast.success('Response copied to clipboard!');
    } catch (error) {
      if (error.name === 'AbortError') return;
      try {
        await navigator.clipboard.writeText(shareText);
        toast.success('Response copied to clipboard!');
      } catch {
        toast.info('Could not copy to clipboard');
      }
    }
  };

  const fetchSessions = async () => {
    try {
      setLoadingSessions(true);
      const response = await aiAPI.getUserSessions();
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Failed to load chat history');
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadSession = async (session) => {
    try {
      if (streamIntervalRef.current) {
        clearInterval(streamIntervalRef.current);
        streamIntervalRef.current = null;
      }
      setHistoryOpen(false);
      setLoading(true);
      
      // Fetch full session history
      const response = await aiAPI.getChatHistory(session.sessionId);
      const messages_data = response.data.messages || [];
      
      console.log('Loading session:', session.sessionId);
      console.log('History data:', messages_data);
      
      // Convert to message format
      const loadedMessages = [INITIAL_MESSAGE];
      
      // Add all messages from history
      // Backend stores messages with 'role' and 'content' fields
      messages_data.forEach((msg, index) => {
        if (msg.role === 'user') {
          loadedMessages.push({
            id: Date.now() + index,
            type: 'user',
            text: msg.content,
            isComplete: true
          });
        } else if (msg.role === 'assistant') {
          loadedMessages.push({
            id: Date.now() + index,
            type: 'ai',
            text: msg.content,
            isComplete: true,
            imageUrl: msg.imageUrl || null
          });
        }
      });
      
      console.log('Loaded messages:', loadedMessages);
      
      setMessages(loadedMessages);
      setSessionId(session.sessionId);
      setSelectedCategory(null);
      setLoading(false);
      
      // Scroll to bottom after loading
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
      
      toast.success('Session loaded!');
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Failed to load session');
      setLoading(false);
    }
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await aiAPI.deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.sessionId !== sessionId));
      toast.success('Session deleted');
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Failed to delete session');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recent';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Recent';
      
      const now = new Date();
      const diffInMs = now - date;
      const diffInHours = diffInMs / (1000 * 60 * 60);
      
      if (diffInHours < 1) {
        return 'Just now';
      } else if (diffInHours < 24) {
        return 'Today';
      } else if (diffInHours < 48) {
        return 'Yesterday';
      } else if (diffInHours < 168) {
        // Less than 7 days
        const days = Math.floor(diffInHours / 24);
        return `${days} days ago`;
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    } catch (error) {
      return 'Recent';
    }
  };


  return (
    <div className="min-h-screen bg-background pb-36 md:pb-28">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 pt-20">
        <div className="py-6">
          {/* Header */}
          <div className="flex flex-col items-center text-center mb-4">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center mb-2 bg-primary"
            >
              <CoachIcon className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-foreground">iCoach</h1>
            <p className="text-muted-foreground text-sm">Your AI Sports Performance Advisor</p>
            
            {/* New Chat & History Buttons */}
            <div className="flex gap-2 mt-3 w-full max-w-md">
              <Button 
                variant="outline" 
                className="flex-1 h-9 rounded-xl text-sm"
                onClick={() => {
                  if (streamIntervalRef.current) {
                    clearInterval(streamIntervalRef.current);
                    streamIntervalRef.current = null;
                  }
                  setMessages([INITIAL_MESSAGE]);
                  setSessionId(null);
                  setSelectedCategory(null);
                  setLoading(false);
                  fullTextsRef.current = {};
                  iCoachService.clear();
                  localStorage.removeItem(msgKey);
                  localStorage.removeItem(sidKey);
                  toast.success('New chat started!');
                }}
              >
                <Calendar className="w-4 h-4 mr-2" />
                New Chat
              </Button>

              <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex-1 h-9 rounded-xl text-sm"
                    onClick={() => {
                      if (!historyOpen) {
                        fetchSessions();
                      }
                    }}
                  >
                    <History className="w-4 h-4 mr-2" />
                    History
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="center">
                  <div className="p-3 border-b border-border">
                    <h3 className="font-semibold text-sm text-foreground">Chat History</h3>
                    <p className="text-xs text-muted-foreground">Resume previous conversations</p>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {loadingSessions ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-5 h-5 animate-spin text-primary" />
                      </div>
                    ) : sessions.length === 0 ? (
                      <div className="py-8 px-4 text-center text-sm text-muted-foreground">
                        No chat history yet
                      </div>
                    ) : (
                      <div className="py-1">
                        {sessions.map((session) => (
                          <div
                            key={session.sessionId}
                            className="px-3 py-3 hover:bg-muted/50 cursor-pointer border-b border-border/50 last:border-0 transition-colors"
                            onClick={() => loadSession(session)}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-medium text-primary">
                                    {formatDate(session.createdAt)}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {session.messageCount || 0} messages
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground line-clamp-2">
                                  {session.lastMessage || 'No preview available'}
                                </p>
                              </div>
                              <button
                                onClick={(e) => deleteSession(session.sessionId, e)}
                                className="text-muted-foreground hover:text-destructive transition-colors p-1 rounded hover:bg-destructive/10"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Category Cards - Show when no messages yet */}
          {messages.length === 1 && (
            <div className="mb-6">
              <h2 className="text-lg font-bold text-foreground mb-4 text-center">What Can I Help You With?</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                {categories.map((category) => {
                  const Icon = category.icon;
                  return (
                    <Card
                      key={category.id}
                      className={`cursor-pointer hover:shadow-lg transition-all rounded-xl md:rounded-2xl border-0 ${
                        selectedCategory === category.id ? 'ring-2 ring-offset-2' : ''
                      }`}
                      style={selectedCategory === category.id ? { ringColor: category.color } : {}}
                      onClick={() => setSelectedCategory(category.id)}
                    >
                      <CardContent className="p-3 md:p-4 flex flex-col items-center text-center">
                        <div 
                          className={`w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-gradient-to-br ${category.gradient} flex items-center justify-center mb-2 md:mb-3`}
                        >
                          <Icon className="w-4 h-4 md:w-5 md:h-5 text-white" />
                        </div>
                        <h3 className="font-semibold text-xs md:text-sm text-foreground mb-0.5 md:mb-1">{category.name}</h3>
                        <p className="text-[10px] md:text-xs text-muted-foreground">{category.prompts.length} quick prompts</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Show prompts when category selected */}
              {selectedCategory && (
                <Card className="mt-4 rounded-2xl border-0 shadow-sm">
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-foreground mb-3">
                      {categories.find(c => c.id === selectedCategory)?.name} - Quick Start:
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {categories.find(c => c.id === selectedCategory)?.prompts.map((prompt, index) => (
                        <Button
                          key={index}
                          variant="outline"
                          className="text-left justify-start h-auto py-3 px-4 hover:bg-muted/50 rounded-xl text-sm"
                          onClick={() => handleSend(prompt)}
                        >
                          {prompt}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Messages Area */}
          {messages.length > 1 && (
            <Card className="mb-4 rounded-2xl border-0 shadow-sm">
              <CardContent className="p-3 md:p-4">
                <div className="space-y-4 max-h-[500px] overflow-y-auto" ref={chatContainerRef} onScroll={handleScroll}>
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`flex items-start gap-2 max-w-[95%] md:max-w-[85%] ${
                        message.type === 'user' ? 'flex-row-reverse' : ''
                      }`}>
                        <Avatar className="w-7 h-7 md:w-8 md:h-8 ring-2 ring-primary/20 flex-shrink-0">
                          {message.type === 'ai' ? (
                            <div className="w-full h-full flex items-center justify-center bg-primary rounded-full">
                              <CoachIcon className="w-3.5 h-3.5 text-white" />
                            </div>
                          ) : (
                            <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs">You</AvatarFallback>
                          )}
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          {/* Show image if available - before message */}
                          {message.type === 'ai' && message.imageUrl && (
                            <div className="mb-2 rounded-lg overflow-hidden">
                              <img 
                                src={message.imageUrl} 
                                alt="Sport visual" 
                                className="w-full max-h-40 object-cover"
                              />
                            </div>
                          )}
                          
                          <div
                            className={`rounded-xl px-3 py-2 ${
                              message.type === 'user'
                                ? 'bg-primary text-white'
                                : 'bg-muted/50 text-foreground'
                            }`}
                          >
                            {message.type === 'ai' ? (
                              <div className="prose prose-sm max-w-none dark:prose-invert 
                                prose-headings:text-sm prose-headings:font-semibold prose-headings:text-foreground prose-headings:mt-3 prose-headings:mb-1
                                prose-p:text-xs prose-p:text-foreground prose-p:leading-relaxed prose-p:my-1
                                prose-strong:text-foreground prose-strong:font-semibold prose-strong:text-xs
                                prose-ul:text-xs prose-ul:my-1 prose-ul:pl-4
                                prose-ol:text-xs prose-ol:my-1 prose-ol:pl-4
                                prose-li:text-xs prose-li:text-foreground prose-li:my-0.5
                                prose-table:text-foreground prose-table:my-2
                                prose-code:text-xs prose-code:bg-muted prose-code:px-1 prose-code:rounded
                                prose-pre:text-xs prose-pre:bg-muted prose-pre:p-2 prose-pre:rounded-lg">
                                <ReactMarkdown 
                                  remarkPlugins={[remarkGfm]}
                                  components={{
                                    h1: ({node, ...props}) => (
                                      <h1 className="text-sm font-semibold mt-2 mb-1" {...props} />
                                    ),
                                    h2: ({node, ...props}) => (
                                      <h2 className="text-sm font-semibold mt-2 mb-1" {...props} />
                                    ),
                                    h3: ({node, ...props}) => (
                                      <h3 className="text-xs font-semibold mt-2 mb-1" {...props} />
                                    ),
                                    p: ({node, ...props}) => (
                                      <p className="text-xs leading-relaxed my-1" {...props} />
                                    ),
                                    ul: ({node, ...props}) => (
                                      <ul className="text-xs list-disc pl-4 my-1 space-y-0.5" {...props} />
                                    ),
                                    ol: ({node, ...props}) => (
                                      <ol className="text-xs list-decimal pl-4 my-1 space-y-0.5" {...props} />
                                    ),
                                    li: ({node, ...props}) => (
                                      <li className="text-xs" {...props} />
                                    ),
                                    table: ({node, ...props}) => (
                                      <div className="overflow-x-auto my-2">
                                        <table className="min-w-full border-collapse text-xs" {...props} />
                                      </div>
                                    ),
                                    th: ({node, ...props}) => (
                                      <th className="border border-border bg-primary text-white px-2 py-1.5 text-left font-semibold text-xs" {...props} />
                                    ),
                                    td: ({node, ...props}) => (
                                      <td className="border border-border px-2 py-1.5 text-xs" {...props} />
                                    ),
                                    tr: ({node, ...props}) => (
                                      <tr className="hover:bg-muted/30 transition-colors" {...props} />
                                    ),
                                    strong: ({node, ...props}) => (
                                      <strong className="font-semibold text-xs" {...props} />
                                    )
                                  }}
                                >
                                  {message.text}
                                </ReactMarkdown>
                              </div>
                            ) : (
                              <p className="text-xs whitespace-pre-wrap leading-relaxed">{message.text}</p>
                            )}
                          </div>
                          
                          {/* Action buttons for AI responses */}
                          {message.type === 'ai' && message.id !== 1 && (
                            <div className="flex items-center space-x-2 mt-1.5 ml-1">
                              <button
                                onClick={() => handleSaveResponse(message)}
                                className="text-[10px] text-muted-foreground hover:text-primary transition-colors flex items-center space-x-1"
                              >
                                <Bookmark className="w-2.5 h-2.5" />
                                <span>Save</span>
                              </button>
                              <button
                                onClick={() => handleShareResponse(message)}
                                className="text-[10px] text-muted-foreground hover:text-primary transition-colors flex items-center space-x-1"
                              >
                                <Share2 className="w-2.5 h-2.5" />
                                <span>Share</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {loading && !messages.some(m => !m.isComplete && m.text) && (
                    <div className="flex justify-start">
                      <div className="flex items-start gap-2 max-w-[95%] md:max-w-[85%]">
                        <Avatar className="w-7 h-7 md:w-8 md:h-8 ring-2 ring-primary/20 flex-shrink-0">
                          <div className="w-full h-full flex items-center justify-center bg-primary rounded-full">
                            <CoachIcon className="w-3.5 h-3.5 text-white" />
                          </div>
                        </Avatar>
                        <div className="rounded-xl px-4 py-3 bg-muted/50">
                          <div className="flex items-center gap-2">
                            <div className="flex gap-1">
                              <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                              <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                              <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                            </div>
                            <span className="text-xs text-muted-foreground ml-1">iCoach is thinking...</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

        </div>
      </div>

      {/* Input Area - Fixed at bottom */}
      <div className="fixed bottom-16 md:bottom-0 left-0 right-0 z-40 bg-gradient-to-t from-background via-background to-transparent pt-4 pb-2 md:pb-4 px-4">
        <div className="max-w-6xl mx-auto">
          <Card className="rounded-2xl border-2 border-primary/30 shadow-xl ring-2 ring-primary/10">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center gap-2">
                <Textarea
                  placeholder="Ask me anything about sports, training, nutrition, recovery..."
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  className="min-h-[52px] md:min-h-[58px] resize-none rounded-xl border-2 border-primary/20 focus:border-primary focus-visible:ring-2 focus-visible:ring-primary/30 bg-muted/30 text-sm transition-all pt-3 pb-3 pl-4"
                />
                <button
                  onClick={() => handleSend()}
                  disabled={!inputText.trim() || loading}
                  className="h-10 w-10 md:h-12 md:w-12 rounded-xl transition-all duration-300 flex-shrink-0 shadow-lg hover:shadow-xl hover:scale-105 hover:opacity-90 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'hsl(271, 76%, 53%)' }}
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-white" />
                  ) : (
                    <Send className="w-4 h-4 md:w-5 md:h-5 text-white" />
                  )}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

    </div>
  );
};

export default AskAI;
